/* oxlint-disable complexity */
import { useRef } from 'react';
import type React from 'react';

import { Separator } from '@change/ds-core-ui';
import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { SignFormAddressRemovalTracking } from '#src/app/pages/petitionDetails/shared/components';
import {
	useFirstMovement,
	useIsEndorsementsPetitionEnabled,
	usePhoto,
	useStickyBehavior,
	PublicFieldContextProvider,
} from '#src/app/pages/petitionDetails/shared/hooks';
import {
	AfterDescriptionWaypoint,
	AfterMobileActionButtonWaypoint,
} from '#src/app/pages/petitionDetails/shared/waypoints';
import { MembershipAwareness } from '#src/app/shared/components/membership';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { Heading } from '../../components/Heading';
import { HeadingMobile } from '../../components/HeadingMobile';
import { HeadingMobileWithPhoto } from '../../components/HeadingMobileWithPhoto';
import { HeadingWithPhoto } from '../../components/HeadingWithPhoto';
import { PetitionStarter } from '../../components/PetitionStarter';
import { VideoFirstHero, useVideoFirstHeroStatus } from '../../components/VideoFirstHero';
import { DecisionMakerSection } from '../DecisionMakerSection';
import { DescriptionContainer } from '../description';
import { EndorsementsContainer } from '../endorsements';
import { MediaHitsContainer } from '../MediaHitsContainer';
import { MobileActionButtonContainer } from '../mobileActionButton';
import { MomentumBarExperimentContent } from '../momentumBar';
import { MovementContainer } from '../Movement';
import { PetitionTimestamp } from '../PetitionTimestamp';
import { ReportAbuseContainer } from '../ReportAbuse';
import { SapCtaContainer } from '../SapCta';
import { ScrollTrackingWaypointContainer } from '../ScrollTrackingWaypoint';
import { SharePetition } from '../sharePetition/SharePetition';
import { SideContainer } from '../side';
import { SignatureStatsMobileContainer } from '../stats/signatureStats';
import { TagBreadcrumbs } from '../TagBreadcrumbs';
import { TheSupporters } from '../TheSupporters';
import {
	SupporterVoicesFeedProvider,
	useSupporterVoicesFeedContext,
} from '../TheSupporters/components/SupporterVoices/components/SupporterVoicesFeed/shared/context';
import { UpdatesContainer } from '../Updates';

import { useVideoFirstExperiment } from './hooks/useVideoFirstExperiment';

const VideoFirstMovement: React.FC<{ hasPetitionPhoto: boolean }> = ({ hasPetitionPhoto }) => {
	const firstMovement = useFirstMovement();
	const movementPhoto = usePhoto();

	if (!firstMovement) return null;

	return (
		<div className={hasPetitionPhoto ? 'mb-4 px-4 sm:mb-2 sm:px-0' : 'mb-4 px-4 md:mb-2 md:px-0'}>
			<MovementContainer movement={firstMovement} alwaysExpanded={!movementPhoto} />
		</div>
	);
};

// oxlint-disable-next-line max-lines-per-function
const MainContainerInner: React.FC = () => {
	const {
		data: {
			petition: { id: petitionId, slug, photo },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { showSupporterVoicesFeed },
	} = useSupporterVoicesFeedContext();
	const {
		data: {
			signed,
			experiments: { signFormGdprRadioRemovalExperiment },
		},
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const canBeSticky = useStickyBehavior(signatureBlockRef);

	const isEndorsementsEnabled = useIsEndorsementsPetitionEnabled();

	const { experiment: videoFirstExperiment, hasVideoFirstSource, videoFirstSourceUrl } = useVideoFirstExperiment();
	const videoFirstHeroStatus = useVideoFirstHeroStatus(videoFirstSourceUrl);
	const usesVideoFirstHeroLayout = videoFirstHeroStatus !== 'fallback';

	const desktopHeadingClassName = cn('col-span-full hidden w-full md:row-start-1 md:block', {
		'lg:col-start-2 lg:-col-end-1': photo === null,
	});
	const defaultDesktopHeading = (
		<div className={desktopHeadingClassName}>{photo === null ? null : <HeadingWithPhoto />}</div>
	);
	const videoFirstDesktopFallback = (
		<div className={desktopHeadingClassName}>{photo === null ? <Heading /> : <HeadingWithPhoto />}</div>
	);

	const defaultMobileHeading = photo === null ? <HeadingMobile /> : <HeadingMobileWithPhoto />;
	const videoFirstMobileFallback =
		photo === null ? <HeadingMobile showMovement={false} /> : <HeadingMobileWithPhoto showMovement={false} />;
	const desktopHeading = videoFirstSourceUrl ? (
		<VideoFirstHero
			fallback={videoFirstDesktopFallback}
			sourceUrl={videoFirstSourceUrl}
			status={videoFirstHeroStatus}
			viewport="desktop"
		/>
	) : (
		defaultDesktopHeading
	);
	const mobileHeading = videoFirstSourceUrl ? (
		<>
			<VideoFirstHero
				fallback={videoFirstMobileFallback}
				sourceUrl={videoFirstSourceUrl}
				status={videoFirstHeroStatus}
				viewport="mobile"
			/>
			<VideoFirstMovement hasPetitionPhoto={photo !== null} />
		</>
	) : (
		defaultMobileHeading
	);
	const sidePanelStartsInSecondRow = usesVideoFirstHeroLayout || (photo === null && videoFirstSourceUrl !== undefined);

	return (
		<PublicFieldContextProvider>
			{/*
			 * Outside both columns because exposure must fire in every arm, and outside the sign form: the
			 * desktop sidebar and mobile sign modal each mount their own, and exposure dedup is per mount.
			 */}
			<SignFormAddressRemovalTracking />
			<Grid className="grid-rows-[auto] px-0 lg:max-w-[1376px] lg:grid-cols-14! lg:grid-rows-[auto]">
				{desktopHeading}

				<div
					className={cn('col-span-full max-md:px-0 md:col-span-5 md:container lg:col-start-2! lg:col-end-11!', {
						'md:row-start-2': !usesVideoFirstHeroLayout && photo !== null,
						'md:row-start-3': usesVideoFirstHeroLayout,
					})}
				>
					{mobileHeading}

					<div className="p-4 md:p-0">
						<div className="hidden md:block">
							<MomentumBarExperimentContent petitionId={petitionId} />
							<DescriptionContainer />

							<ReportAbuseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}
							<PetitionStarter />
						</div>

						<div className="block md:hidden">
							<MobileActionButtonContainer
								layout={usesVideoFirstHeroLayout ? 'sticky-sign' : undefined}
								sticky={false}
								variant="v1"
							/>

							<AfterMobileActionButtonWaypoint />
							<SignatureStatsMobileContainer />

							<MomentumBarExperimentContent petitionId={petitionId} />
							<DescriptionContainer />

							<ReportAbuseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}

							<PetitionStarter />
						</div>

						<ScrollTrackingWaypointContainer name="scroll_past_body" />
						<AfterDescriptionWaypoint />
					</div>
				</div>
				{/* We need to modify the row-span to match the exact last row before the first full width module */}
				<div
					data-qa="second-column-container"
					className={cn(
						'h-0 md:col-start-6 md:col-end-9 md:row-span-3 md:mb-20 md:h-full lg:col-start-11 lg:col-end-15',
						sidePanelStartsInSecondRow ? 'md:row-start-2' : 'md:row-start-1',
					)}
				>
					<div className="block md:hidden">
						<MobileActionButtonContainer
							layout={usesVideoFirstHeroLayout ? 'sticky-sign' : undefined}
							sticky={true}
							variant="v1"
						/>
					</div>
					<div
						data-qa="sticky-action-panel-container"
						className={cn(
							'relative top-6 right-0 z-12 mt-0 hidden md:mt-6! md:mr-6! md:block md:max-w-[350px] lg:mt-6! lg:mr-6 lg:max-w-[350px]',
							{
								'!sticky': canBeSticky,
							},
						)}
					>
						<div>
							<SideContainer containerRef={signatureBlockRef} />
						</div>
					</div>
				</div>

				{hasVideoFirstSource && <TrackExperimentExposure experiment={videoFirstExperiment} resetKeys={[petitionId]} />}

				{/*
				 * Desktop sidebar and mobile sign modal both mount their own sign form concurrently, so this
				 * must live here rather than inside the sign form.
				 */}
				<TrackExperimentExposure experiment={signFormGdprRadioRemovalExperiment} resetKeys={[petitionId]} />

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SapCtaContainer />

					{/* Shares this cell to preserve the body-column width without adding another Grid row gap. */}
					<MediaHitsContainer className="mb-20" />

					<DecisionMakerSection />
				</div>

				<div
					className={cn('col-span-full', {
						// Mixed feed: full-width breakout for the wide card grid.
						'my-6 px-4 md:px-0': showSupporterVoicesFeed,
						// Legacy comments/videos fallback: centered in the page grid, matching the
						// other petition-body sections (updates, share, etc.).
						'container lg:col-start-2 lg:col-end-11': !showSupporterVoicesFeed,
					})}
				>
					<TheSupporters showSupporterVoicesFeed={showSupporterVoicesFeed} />
				</div>

				<div className="col-span-full my-6 px-4 md:px-0">
					<MembershipAwareness sourceLocation="petition_details" petitionId={petitionId} slug={slug} signed={signed} />
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<ScrollTrackingWaypointContainer name="scroll_updates_section" />

					<Separator className="mt-14 mb-8 md:mt-20" />

					<UpdatesContainer />
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SharePetition />

					<Separator className="mt-14 mb-6 md:mt-20" />
					<PetitionTimestamp />
					<Separator className="my-6" />
					<TagBreadcrumbs />
				</div>
			</Grid>
		</PublicFieldContextProvider>
	);
};

/**
 * Wraps the page content in the supporter-voices feed provider, so the feed provider holds the
 * single supporter-voices feed instance and the page layout decision (showSupporterVoicesFeed,
 * read in MainContainerInner) and the feed render (SupporterVoicesFeed, nested below) share one
 * `useSupporterVideos` state. See CHANGE-94834.
 */
export const MainContainer: React.FC = () => {
	return (
		<SupporterVoicesFeedProvider>
			<MainContainerInner />
		</SupporterVoicesFeedProvider>
	);
};
